import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Icon, Progress } from 'antd';

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '16px',
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: '12px',
  padding: '40px 48px 36px',
  minWidth: '280px',
  maxWidth: '420px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
};

const iconStyle = {
  fontSize: 54,
  color: '#1890ff',
};

const messageStyle = {
  marginTop: '20px',
  fontSize: '18px',
  fontWeight: 600,
  textAlign: 'center',
};

const progressContainerStyle = {
  width: '100%',
  marginTop: '28px',
};

/**
 * @component
 * @name OverlayFullScreenModal
 * @description Full screen loading overlay rendered in a portal with a staged
 * progress indicator for long-running quote requests.
 * @param {Object} props Component props
 * @param {boolean} props.showModal Whether to show overlay full screen modal
 */
const MIN_PROGRESS = 6;
const AUTO_PROGRESS_CAP = 96;
const FULL_PROGRESS = 100;
const TARGET_DURATION_MS = 45000; // aim to reach ~96% in 45 seconds
const SECOND_MESSAGE_MS = 10000; // show second message after 10 seconds
const THIRD_MESSAGE_MS = 30000; // show third message after 25 seconds

const OverlayFullScreenModal = ({ showModal, progressOverride }) => {
  const previousOverflow = useRef();
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Thinking...');

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    if (showModal) {
      previousOverflow.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    } else if (previousOverflow.current !== undefined) {
      document.body.style.overflow = previousOverflow.current;
    }

    return () => {
      if (typeof document === 'undefined') return;
      if (previousOverflow.current !== undefined) {
        document.body.style.overflow = previousOverflow.current;
      } else {
        document.body.style.removeProperty('overflow');
      }
    };
  }, [showModal]);

  useEffect(() => {
    if (!showModal || typeof document === 'undefined') return undefined;

    const activeElement = document.activeElement;
    if (activeElement && activeElement instanceof HTMLElement) {
      activeElement.blur();
    }

    return undefined;
  }, [showModal]);

  useEffect(() => {
    if (!showModal) {
      setProgress(0);
      setStatusMessage('Thinking...');
      return undefined;
    }

    setProgress(MIN_PROGRESS);
    setStatusMessage('Thinking...');

    const startTime = Date.now();

    const progressInterval = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const computedProgress = Math.min(
        MIN_PROGRESS + (elapsed / TARGET_DURATION_MS) * (AUTO_PROGRESS_CAP - MIN_PROGRESS),
        AUTO_PROGRESS_CAP
      );
      setProgress((prev) => {
        if (prev >= FULL_PROGRESS) return prev;
        return Math.max(prev, Math.round(computedProgress));
      });
    }, 500);

    const messageTimers = [
      window.setTimeout(() => {
        setStatusMessage('Generating Personalized Quotes...');
      }, SECOND_MESSAGE_MS),
      window.setTimeout(() => {
        setStatusMessage('Gathering Sailing Schedules...');
      }, THIRD_MESSAGE_MS),
    ];

    return () => {
      window.clearInterval(progressInterval);
      messageTimers.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return undefined;
    if (typeof progressOverride !== 'number') return undefined;

    if (progressOverride >= FULL_PROGRESS) {
      setProgress(FULL_PROGRESS);
      setStatusMessage('Generating Personalized Quotes...');
      return undefined;
    }

    setProgress((prev) => Math.max(prev, Math.round(progressOverride)));
    return undefined;
  }, [progressOverride, showModal]);

  if (!showModal || typeof document === 'undefined') {
    return null;
  }

  return ReactDOM.createPortal(
    <div role="dialog" aria-modal="true" aria-label="Loading" style={overlayStyle}>
      <div style={cardStyle}>
        <div role="status" aria-live="polite" style={{ textAlign: 'center' }}>
          <Icon type="loading" style={iconStyle} spin />
          <div style={messageStyle}>{statusMessage}</div>
        </div>
        <div style={progressContainerStyle}>
          <Progress percent={progress} showInfo={false} strokeWidth={6} strokeColor="#1890ff" />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OverlayFullScreenModal;
